<template>
    <v-content class="public-header-wrapper">
        <!-- 📱 Mobile Navigation Drawer (Translucent light glass framework layout) -->
        <v-navigation-drawer 
            right 
            light 
            dense 
            v-model="drawer" 
            enable-resize-watcher 
            fixed 
            app 
            width="180" 
            v-if="isMobile"
            class="light-glass-drawer"
        >
            <v-list dense class="pt-3">
                <v-list-tile ripple @click="viewHelp">
                    <v-list-tile-action class="mr-2">
                        <v-icon color="primary">auto_stories</v-icon>
                    </v-list-tile-action>
                    <v-list-tile-content>
                        <v-list-tile-title class="drawer-link-text">Knowledge Hub</v-list-tile-title>
                    </v-list-tile-content>
                </v-list-tile>
                
                <v-list-tile ripple @click="dialogContactUs=true;drawer=false">
                    <v-list-tile-action class="mr-2">
                        <v-icon color="primary">email</v-icon>
                    </v-list-tile-action>
                    <v-list-tile-content>
                        <v-list-tile-title class="drawer-link-text">Contact Us</v-list-tile-title>
                    </v-list-tile-content>
                </v-list-tile>
            </v-list>
        </v-navigation-drawer>

        <!-- 🖥️ Standard Fixed Transparent Desktop Navbar Toolbar Layer -->
        <v-toolbar fixed flat clipped-right class="transparent-public-toolbar">
            
            <div class="logo-branding-block d-flex align-center" @click="viewPortalSite">
                <img src="@/assets/logo/new/idea3.png" alt="logo" class="logo-image">
                <span class="logo-text">SweldoNow</span>
                <span v-if="environment" class="orange--text ml-10 mt-5 subheading">
                    ({{ environment }})
                </span>
            </div>

            <v-spacer></v-spacer>
            
            <!-- Desktop Layout Buttons (Protected structural parent wrapper class bounds) -->
            <v-toolbar-items class="hidden-sm-and-down align-center">
               
                <!-- 🔔 Desktop Notification Bell + Text Label (Visible if unread counts exist) -->
                <v-btn 
                    flat 
                    class="no-uppercase brand-dark-blue-text mx-2 header-notif-btn" 
                    @click="openNotifications" 
                    title="View System Announcements"
                >
                    <v-badge overlap small color="orange" class="mr-2">
                        <template v-slot:badge v-if="notificationsCount > 0">
                            <span class="white--text font-weight-bold">{{notificationsCount}}</span>
                        </template>
                        <v-icon class="header-icon-accent">notifications_active</v-icon>
                    </v-badge>
                    
                    <!-- 💡 Fixed: Stripped hidden utility class to guarantee text remains readable on desktop/laptops -->
                    <span class="label-text notif-label-text">Announcements</span>
                </v-btn>

                <!-- Knowledge Hub Action Link Trigger -->
                <v-btn flat :class="['no-uppercase', getLinkStyle('userguide')]" @click="viewHelp">
                    <v-icon left size="20">auto_stories</v-icon>
                    <span class="label-text">Knowledge Hub</span>
                </v-btn>
                
                <!-- Contact Us Action Link Trigger -->
                <v-btn flat :class="['no-uppercase', getLinkStyle('contact')]" @click="dialogContactUs=true">
                    <v-icon left size="20">email</v-icon>
                    <span class="label-text">Contact Us</span>
                </v-btn>
            </v-toolbar-items>

            <!-- Mobile Layout Accessories: Notification Badge + Slide-out Trigger Trigger Buttons -->
            <v-toolbar-side-icon v-if="isMobile" class="header-action-btn mr-1" @click="openNotifications">
                <v-badge overlap small color="orange">
                    <template v-slot:badge v-if="notificationsCount > 0">
                        <span class="white--text font-weight-bold">{{notificationsCount}}</span>
                    </template>
                    <v-icon class="header-icon-accent">notifications_active</v-icon>
                </v-badge>
            </v-toolbar-side-icon>

            <v-toolbar-side-icon v-if="isMobile" class="header-action-btn" @click.stop="drawer = !drawer">
                <v-icon class="header-icon-accent">menu</v-icon>
            </v-toolbar-side-icon>
        </v-toolbar>

        <!-- Global Modals System Overlays Container Section -->
        <v-dialog v-model="dialogContactUs" max-width="800" hide-overlay>   
            <contact-us :dialogContactUs.sync="dialogContactUs"></contact-us>
        </v-dialog>
        
        <!-- 🔔 Public Announcements Popup Dialog Wrapper -->
        <v-dialog 
            v-model="dialogNotifications" 
            :fullscreen="isMobile" 
            max-width="800" 
            hide-overlay 
            content-class="left-aligned-dialog"
        >
            <v-card class="layout-dialog-card rounded-4">
                <v-toolbar flat height="55px" class="dialog-inner-toolbar">
                    <v-icon color="primary" class="mr-2">notifications_active</v-icon>
                    <span class="font-weight-bold dialog-title-text">Announcements</span>
                    <v-spacer></v-spacer>
                    <v-btn icon class="dialog-close-btn" @click.native="dialogNotifications=false">
                        <v-icon>close</v-icon>
                    </v-btn>
                </v-toolbar>
                <public-notifications 
                    :dialogNotifications.sync="dialogNotifications" 
                    @notifications-count="updateLiveHeaderBadgeCount"
                ></public-notifications>
            </v-card>
        </v-dialog>
        
    </v-content>
</template>

<script>
export default {
    data () {
        return {
            drawer: false,
            dialogContactUs: false,
            dialogUpgrade: false,
            dialogNotifications: false,
            liveEmittedCount: 0, // New data property state
            badgeCleared: false // New flag to hide badge instantly on click
        }
    },   
    props: ['current_page'],
    components: {
        'contact-us': () => import('@/components/Common/ContactUs'),
        'public-notifications': () => import('@/components/PublicNotifications/PublicNotifications'),
    },
    methods: {
        getLinkStyle(page) {
            /**
             * Critical Layout Correction:
             * Changed 'white--text' to 'brand-dark-blue-text' to match the 
             * light background requirements.
             */
            let header_style = "font-weight-medium brand-dark-blue-text"
            
            if (this.current_page == page) {
                header_style = "font-weight-bold brand-primary-blue-text"
            }

            if (this.current_page == 'notauthorized') {
                header_style = "font-weight-bold grey--text text--darken-2"
            }

            return header_style
        },
        viewPortalSite() {
            window.open("https://www.sweldonow.ph")
        },
        viewHelp() {
            window.open("https://help.sweldonow.ph")

            if (this.isMobile) {
                this.drawer=false
            }
        },
        updateLiveHeaderBadgeCount(count) {
            this.liveEmittedCount = count;
            // Reset the flag if the child component actually finds brand new notifications later
            if (count > 0) {
                this.badgeCleared = false;
            }
        },
        openNotifications() {
            this.dialogNotifications = true;
            this.badgeCleared = true; 
            
            const todayStr = this.$moment().format('YYYY-MM-DD');
            localStorage.setItem('publicNotificationsLastRead', Date.now()); 
            localStorage.setItem('holidayLastReadDate', todayStr); // Mark today's holiday as read
        },
    },
    computed: {
        notificationsCount() {
            if (this.badgeCleared) return 0; 
        
            if (this.liveEmittedCount > 0) {
                return this.liveEmittedCount;
            }

            // Fallback calculations for local storage
            const cachedData = localStorage.getItem('publicNotifications');
            const notifications = cachedData ? JSON.parse(cachedData) : [];
            const lastRead = localStorage.getItem('publicNotificationsLastRead') || 0;
            
            const unreadNotif = notifications.filter(notif => {
                return this.$moment(notif.createdOn).valueOf() > Number(lastRead);
            });

            // Holiday fallback calculation
            const holidayLastSeenDate = localStorage.getItem('holidayLastReadDate');
            const todayStr = this.$moment().format('YYYY-MM-DD');
            
            // We replicate your child logic checking if a cached holiday needs alerting
            let holidayCount = 0;
            if (this.todayHolidays && this.todayHolidays.length > 0 && holidayLastSeenDate !== todayStr) {
                holidayCount = this.todayHolidays.length;
            }
            
            return unreadNotif.length + holidayCount;
        },
        environment() {
            const url = window.location.href;
            const isMobile = this.isMobile;

            if (url.includes('dev')) {
            return isMobile ? 'Local' : 'Local Environment';
            }
            if (url.includes('test')) {
            return isMobile ? 'Test' : 'Test Environment';
            }
            return '';
        }
    },
}
</script>

<style scoped>
/* ==========================================================================
   1. LOGO BRANDING BLOCK
   ========================================================================== */
.public-header-wrapper .logo-branding-block {
    cursor: pointer;
    display: flex;
    align-items: center;
}

.public-header-wrapper .logo-image {
    width: 45px;
    height: 45px;
    object-fit: contain;
    margin-right: 10px;
    display: inline-block;
}

.public-header-wrapper .logo-text {
    font-family: 'Inter', sans-serif !important;
    font-weight: 700 !important; /* Made heavier to stand out beautifully as global branding */
    letter-spacing: -0.04em !important;
    color: #1565C0 !important; /* Switched to elite brand slate blue for cohesive visual harmony */
    font-size: 28px !important;
    display: inline-block;
    user-select: none;
}

/* ==========================================================================
   2. NAVBAR TOOLBAR SYSTEM
   ========================================================================== */
.transparent-public-toolbar {
    background-color: transparent !important;
    box-shadow: none !important;
    padding: 0 12px;
}

.v-toolbar-items.align-center {
    display: flex;
    align-items: center;
}

/* Base custom interactive text element controls mapping */
.label-text {
    font-size: 14px !important;
    font-weight: 600 !important;
    letter-spacing: normal !important;
    color: #1565C0 !important; 
    transition: color 0.2s ease;
}

/* Interactive custom utility class anchors overrides for text bindings */
.brand-dark-blue-text,
.brand-dark-blue-text .v-icon {
    color: #1565C0 !important;
}

.brand-primary-blue-text,
.brand-primary-blue-text .v-icon {
    color: #1976D2 !important;
}

/* Hover transitions across all navbar elements links */
.v-toolbar-items .v-btn:hover .label-text,
.v-toolbar-items .v-btn:hover .v-icon,
.header-notif-btn:hover .notif-label-text {
    color: #1976D2 !important; /* Highlights flawlessly into primary blue on mouse hover */
}

/* ==========================================================================
   3. MOBILE SIDE NAVIGATION OVERLAY DRAWER
   ========================================================================== */
.light-glass-drawer {
    background: rgba(255, 255, 255, 0.85) !important;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-left: 1px solid rgba(25, 118, 210, 0.1) !important;
}

.drawer-link-text {
    color: #1565C0 !important;
    font-weight: 600 !important; /* Set slightly crisper for small device read directions */
    font-size: 12px !important;
}

.header-icon-accent {
    color: #1565C0 !important;
}

.header-action-btn {
    margin: 0 !important;
}

/* ==========================================================================
   4. ANNOUNCEMENTS MODAL CARD DIALOG SYSTEM
   ========================================================================== */
.layout-dialog-card {
    background: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(20px) saturate(120%);
    -webkit-backdrop-filter: blur(20px) saturate(120%);
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    box-shadow: 0 24px 64px rgba(21, 101, 192, 0.18) !important; /* Deep ambient blur shadow lift */
    overflow: hidden;
}

.dialog-inner-toolbar {
    background-color: transparent !important;
    border-bottom: 1px solid rgba(25, 118, 210, 0.08) !important;
    padding: 0 16px;
}

.dialog-title-text {
    color: #1565C0 !important;
    font-size: 18px;
    font-weight: 700;
    font-family: 'Inter', sans-serif !important;
}

.dialog-close-btn .v-icon {
    color: #1565C0 !important;
    transition: color 0.2s ease;
}

.dialog-close-btn:hover .v-icon {
    color: #D32F2F !important; /* Turns into a standard corporate micro-red soft flag on hover close */
}

.dialog-inner-content {
    background-color: transparent !important;
}

/* ==========================================================================
   5. HEADER SPECIFIC UTILITY ANCHORS
   ========================================================================== */
.header-notif-btn {
    display: flex !important;
    align-items: center !important;
    height: 100% !important;
}

.notif-label-text {
    font-size: 14px !important;
    font-weight: 600 !important;
    color: #1565C0 !important;
}

/* left align popup */
::v-deep .left-aligned-dialog {
  position: absolute !important;
  left: 0 !important;
  margin-left: 0 !important;
}
</style>
